import { asListingAttributesRecord } from "@/lib/listing/listingAttributesJson";
import type {
  ListingSpecAttributes,
  ListingSpecValidationIssue,
} from "@/lib/listing/specRequirements";
import {
  buildDiningTableChairAttributesFromForm,
  defaultDiningTableChairSpecFormState,
  isDiningTableChairSubcategory,
  type DiningTableChairSpecFormState,
} from "@/lib/listing/diningTableChairListingSpec";
import {
  composeListingTitleFromSlotsWithIdentity,
  joinListingTitleParts,
  type ListingTitleIdentity,
} from "@/lib/listing/listingTitleTokens";

export {
  LISTING_TITLE_MAX_LENGTH,
  joinListingTitleParts,
  truncateListingTitle,
} from "@/lib/listing/listingTitleTokens";

export const FULL_SEATING_SPEC_SUBCATEGORY_IDS = [
  "chair",
  "sofa-couche",
] as const;
export type FullSeatingSpecSubcategoryId =
  (typeof FULL_SEATING_SPEC_SUBCATEGORY_IDS)[number];

export const MATERIAL_ONLY_SEATING_SUBCATEGORY_IDS = [
  "l-shaped-chair",
  "l-shaped-sofa",
] as const;
export type MaterialOnlySeatingSubcategoryId =
  (typeof MATERIAL_ONLY_SEATING_SUBCATEGORY_IDS)[number];

export const CHAIR_SCOPED_SUBCATEGORY_IDS = [
  ...FULL_SEATING_SPEC_SUBCATEGORY_IDS,
  ...MATERIAL_ONLY_SEATING_SUBCATEGORY_IDS,
] as const;
export type ChairScopedSubcategoryId =
  (typeof CHAIR_SCOPED_SUBCATEGORY_IDS)[number];

export type SeatingSpecUiVariant = "chair" | "sofa";

export const CHAIR_MATERIALS = [
  "leather",
  "suede",
  "plastic",
  "mat",
  "other",
] as const;
export type ChairMaterial = (typeof CHAIR_MATERIALS)[number];

export const CHAIR_QUANTITY_MODES = ["single", "set"] as const;
export type ChairQuantityMode = (typeof CHAIR_QUANTITY_MODES)[number];

/** Sofa family shape — B1: stored on `sofa-couche` as `sofa_shape`. */
export const SOFA_SHAPES = ["standard", "l_shaped"] as const;
export type SofaShape = (typeof SOFA_SHAPES)[number];

export const LEGACY_L_SHAPED_SUBCATEGORY_IDS = [
  "l-shaped-sofa",
  "l-shaped-chair",
] as const;

export const CHAIR_SET_MIN = 2;
export const CHAIR_SET_MAX = 12;
export const CHAIR_PER_SEAT_MIN = 1;
export const CHAIR_PER_SEAT_MAX = 8;
export const CHAIR_TOTAL_CAPACITY_MAX = 12;

export interface ChairSpecFormState {
  /** Required for `sofa-couche`; ignored for chair / legacy L-shaped material-only. */
  sofaShape: SofaShape | "";
  chairQuantityMode: ChairQuantityMode | "";
  chairSetCount: number;
  chairSeatCapacities: Array<number | null>;
  chairMaterial: ChairMaterial | "";
}

export function defaultChairSpecFormState(): ChairSpecFormState {
  return {
    sofaShape: "",
    chairQuantityMode: "",
    chairSetCount: CHAIR_SET_MIN,
    chairSeatCapacities: [null],
    chairMaterial: "",
  };
}

export function isChairLikeSubcategory(
  subcategoryId: string | null | undefined,
): subcategoryId is ChairScopedSubcategoryId {
  if (!subcategoryId) return false;
  return (CHAIR_SCOPED_SUBCATEGORY_IDS as readonly string[]).includes(
    subcategoryId,
  );
}

export function isFullChairSpecSubcategory(subcategoryId: string): boolean {
  return (FULL_SEATING_SPEC_SUBCATEGORY_IDS as readonly string[]).includes(
    subcategoryId,
  );
}

export function isChairMaterialOnlySubcategory(subcategoryId: string): boolean {
  return (MATERIAL_ONLY_SEATING_SUBCATEGORY_IDS as readonly string[]).includes(
    subcategoryId,
  );
}

export function getSeatingSpecUiVariant(
  subcategoryId: string,
): SeatingSpecUiVariant {
  if (subcategoryId === "sofa-couche" || subcategoryId === "l-shaped-sofa") {
    return "sofa";
  }
  return "chair";
}

export function isSofaShape(value: unknown): value is SofaShape {
  return (
    typeof value === "string" &&
    (SOFA_SHAPES as readonly string[]).includes(value)
  );
}

export function formatSofaShapeLabel(shape: SofaShape): string {
  return shape === "l_shaped" ? "L-shaped" : "Standard";
}

/** Legacy L-shaped sibling ids → canonical Sofa (B1). Null if already canonical / unrelated. */
export function resolveCanonicalSofaSubcategoryId(
  subcategoryId: string | null | undefined,
): "sofa-couche" | null {
  if (subcategoryId === "l-shaped-sofa" || subcategoryId === "l-shaped-chair") {
    return "sofa-couche";
  }
  return null;
}

export function sofaShapeFromLegacySubcategoryId(
  subcategoryId: string | null | undefined,
): SofaShape | null {
  if (subcategoryId === "l-shaped-sofa" || subcategoryId === "l-shaped-chair") {
    return "l_shaped";
  }
  return null;
}

function seatingProductNoun(
  subcategoryId: string,
  plural = false,
): "Chair" | "Chairs" | "Sofa" | "Sofas" {
  const isSofa = getSeatingSpecUiVariant(subcategoryId) === "sofa";
  if (isSofa) {
    return plural ? "Sofas" : "Sofa";
  }
  return plural ? "Chairs" : "Chair";
}

export function getSeatingSpecSectionLabel(subcategoryId: string): string {
  if (isFullChairSpecSubcategory(subcategoryId)) {
    return getSeatingSpecUiVariant(subcategoryId) === "sofa"
      ? "Sofa details"
      : "Chair details";
  }
  if (isChairMaterialOnlySubcategory(subcategoryId)) {
    return getSeatingSpecUiVariant(subcategoryId) === "sofa"
      ? "L-shaped sofa details"
      : "L-shaped chair details";
  }
  return "Seating details";
}

/** True when Step 2 should collect sofa_shape (canonical Sofa only). */
export function sofaRequiresShapeField(subcategoryId: string): boolean {
  return subcategoryId === "sofa-couche";
}

/**
 * L-shaped Sofa (D5): no listing type (implicit single); seating capacity optional.
 * Standard Sofa keeps full seating (type + required capacity).
 */
export function sofaUsesSimplifiedLShapedSeating(
  subcategoryId: string,
  sofaShape: unknown,
): boolean {
  return sofaRequiresShapeField(subcategoryId) && sofaShape === "l_shaped";
}

export function formatChairMaterialLabel(material: ChairMaterial): string {
  return material.charAt(0).toUpperCase() + material.slice(1);
}

/**
 * Material word for auto-generated titles.
 * Omit `other` — it is a form catch-all, not a buyer-facing attribute.
 */
export function listingTitleMaterialWord<T extends string>(
  material: string | null | undefined,
  formatLabel: (material: T) => string,
): string {
  if (typeof material !== "string" || !material || material === "other") {
    return "";
  }
  return formatLabel(material as T);
}

export function sumChairCapacities(capacities: readonly number[]): number {
  return capacities.reduce((acc, n) => acc + n, 0);
}

function isChairMaterial(value: unknown): value is ChairMaterial {
  return (
    typeof value === "string" &&
    (CHAIR_MATERIALS as readonly string[]).includes(value)
  );
}

function isChairQuantityMode(value: unknown): value is ChairQuantityMode {
  return (
    typeof value === "string" &&
    (CHAIR_QUANTITY_MODES as readonly string[]).includes(value)
  );
}

function parseCapacitiesArray(value: unknown): number[] | null {
  if (!Array.isArray(value) || value.length === 0) return null;
  const parsed: number[] = [];
  for (const entry of value) {
    if (typeof entry !== "number" || !Number.isInteger(entry)) return null;
    parsed.push(entry);
  }
  return parsed;
}

export function normalizeChairSetCount(count: number): number {
  if (!Number.isFinite(count)) return CHAIR_SET_MIN;
  return Math.min(CHAIR_SET_MAX, Math.max(CHAIR_SET_MIN, Math.round(count)));
}

export function resizeChairCapacitiesForSetCount(
  prev: Array<number | null>,
  setCount: number,
): Array<number | null> {
  const count = normalizeChairSetCount(setCount);
  const next = prev.slice(0, count);
  while (next.length < count) {
    next.push(null);
  }
  return next;
}

export function buildChairAttributesFromForm(
  subcategoryId: string,
  form: ChairSpecFormState,
): ListingSpecAttributes {
  if (isChairMaterialOnlySubcategory(subcategoryId)) {
    if (!isChairMaterial(form.chairMaterial)) {
      return {};
    }
    return { chair_material: form.chairMaterial };
  }

  if (!isFullChairSpecSubcategory(subcategoryId)) {
    return {};
  }

  if (!isChairMaterial(form.chairMaterial)) {
    return {};
  }

  const sofaShapeAttrs: ListingSpecAttributes =
    sofaRequiresShapeField(subcategoryId) && isSofaShape(form.sofaShape)
      ? { sofa_shape: form.sofaShape }
      : {};

  if (sofaUsesSimplifiedLShapedSeating(subcategoryId, form.sofaShape)) {
    const capacity = form.chairSeatCapacities[0];
    if (capacity == null) {
      return { ...sofaShapeAttrs, chair_material: form.chairMaterial };
    }
    return {
      ...sofaShapeAttrs,
      chair_quantity_mode: "single",
      chair_seat_capacities: [capacity],
      chair_material: form.chairMaterial,
    };
  }

  if (form.chairQuantityMode === "single") {
    const capacity = form.chairSeatCapacities[0];
    if (capacity == null) {
      return { ...sofaShapeAttrs, chair_material: form.chairMaterial };
    }
    return {
      ...sofaShapeAttrs,
      chair_quantity_mode: "single",
      chair_seat_capacities: [capacity],
      chair_material: form.chairMaterial,
    };
  }

  if (form.chairQuantityMode === "set") {
    const count = normalizeChairSetCount(form.chairSetCount);
    const capacities = form.chairSeatCapacities.slice(0, count);
    if (capacities.some((c) => c == null)) {
      return { ...sofaShapeAttrs, chair_material: form.chairMaterial };
    }
    return {
      ...sofaShapeAttrs,
      chair_quantity_mode: "set",
      chair_seat_capacities: capacities as number[],
      chair_material: form.chairMaterial,
    };
  }

  return sofaShapeAttrs;
}

export function chairFormFromAttributes(
  subcategoryId: string,
  attributes: unknown,
): ChairSpecFormState {
  const base = defaultChairSpecFormState();
  const record = asListingAttributesRecord(attributes);
  if (!record) {
    return base;
  }
  const material = isChairMaterial(record.chair_material)
    ? record.chair_material
    : "";

  if (isChairMaterialOnlySubcategory(subcategoryId)) {
    return { ...base, chairMaterial: material };
  }

  if (!isFullChairSpecSubcategory(subcategoryId)) {
    return base;
  }

  /** Existing Sofa rows without shape → treat as standard (grandfather). */
  const sofaShape: SofaShape | "" = sofaRequiresShapeField(subcategoryId)
    ? isSofaShape(record.sofa_shape)
      ? record.sofa_shape
      : "standard"
    : "";

  const mode = isChairQuantityMode(record.chair_quantity_mode)
    ? record.chair_quantity_mode
    : "";
  const capacities = parseCapacitiesArray(record.chair_seat_capacities);

  if (sofaUsesSimplifiedLShapedSeating(subcategoryId, sofaShape)) {
    return {
      sofaShape,
      chairQuantityMode: "single",
      chairSetCount: CHAIR_SET_MIN,
      chairSeatCapacities: [capacities?.[0] ?? null],
      chairMaterial: material,
    };
  }

  if (mode === "single" && capacities && capacities.length >= 1) {
    return {
      sofaShape,
      chairQuantityMode: "single",
      chairSetCount: CHAIR_SET_MIN,
      chairSeatCapacities: [capacities[0] ?? null],
      chairMaterial: material,
    };
  }

  if (mode === "set" && capacities && capacities.length >= CHAIR_SET_MIN) {
    const count = capacities.length;
    return {
      sofaShape,
      chairQuantityMode: "set",
      chairSetCount: count,
      chairSeatCapacities: resizeChairCapacitiesForSetCount(capacities, count),
      chairMaterial: material,
    };
  }

  return { ...base, sofaShape, chairMaterial: material };
}

function validatePerChairCapacities(
  capacities: number[],
  subcategoryId: string,
): ListingSpecValidationIssue | null {
  const unit = seatingProductNoun(subcategoryId).toLowerCase();
  for (const capacity of capacities) {
    if (
      !Number.isInteger(capacity) ||
      capacity < CHAIR_PER_SEAT_MIN ||
      capacity > CHAIR_PER_SEAT_MAX
    ) {
      return {
        key: "chair_seat_capacities",
        code: "out_of_range",
        message: `Each ${unit} must seat between ${CHAIR_PER_SEAT_MIN} and ${CHAIR_PER_SEAT_MAX} people.`,
      };
    }
  }

  const total = sumChairCapacities(capacities);
  if (total > CHAIR_TOTAL_CAPACITY_MAX) {
    return {
      key: "chair_seat_capacities",
      code: "out_of_range",
      message: `Total seated capacity cannot exceed ${CHAIR_TOTAL_CAPACITY_MAX} (currently ${total}).`,
    };
  }

  return null;
}

export function validateStoredChairAttributes(
  subcategoryId: string,
  attributes: ListingSpecAttributes | Record<string, unknown>,
): ListingSpecValidationIssue[] {
  const issues: ListingSpecValidationIssue[] = [];
  const record = attributes as Record<string, unknown>;

  if (!isChairMaterial(record.chair_material)) {
    issues.push({
      key: "chair_material",
      code: "required",
      message: `${seatingProductNoun(subcategoryId)} material is required.`,
    });
    return issues;
  }

  if (isChairMaterialOnlySubcategory(subcategoryId)) {
    return issues;
  }

  if (!isFullChairSpecSubcategory(subcategoryId)) {
    return issues;
  }

  if (
    sofaRequiresShapeField(subcategoryId) &&
    !isSofaShape(record.sofa_shape)
  ) {
    issues.push({
      key: "sofa_shape",
      code: "required",
      message: "Please choose standard or L-shaped sofa.",
    });
    return issues;
  }

  if (sofaUsesSimplifiedLShapedSeating(subcategoryId, record.sofa_shape)) {
    const optionalCapacities = parseCapacitiesArray(
      record.chair_seat_capacities,
    );
    if (!optionalCapacities) {
      return issues;
    }
    if (optionalCapacities.length !== 1) {
      issues.push({
        key: "chair_seat_capacities",
        code: "invalid_type",
        message: "L-shaped sofas use a single seating capacity when provided.",
      });
      return issues;
    }
    const capacityIssue = validatePerChairCapacities(
      optionalCapacities,
      subcategoryId,
    );
    if (capacityIssue) {
      issues.push(capacityIssue);
    }
    return issues;
  }

  if (!isChairQuantityMode(record.chair_quantity_mode)) {
    const unit = seatingProductNoun(subcategoryId).toLowerCase();
    issues.push({
      key: "chair_quantity_mode",
      code: "required",
      message: `Please choose single ${unit} or set of ${unit}s.`,
    });
    return issues;
  }

  const capacities = parseCapacitiesArray(record.chair_seat_capacities);
  if (!capacities) {
    issues.push({
      key: "chair_seat_capacities",
      code: "required",
      message: "Seating capacity for each chair is required.",
    });
    return issues;
  }

  if (record.chair_quantity_mode === "single") {
    if (capacities.length !== 1) {
      issues.push({
        key: "chair_seat_capacities",
        code: "invalid_type",
        message: `Single ${seatingProductNoun(subcategoryId).toLowerCase()} listings need exactly one seating capacity.`,
      });
      return issues;
    }
  } else if (
    capacities.length < CHAIR_SET_MIN ||
    capacities.length > CHAIR_SET_MAX
  ) {
    issues.push({
      key: "chair_seat_capacities",
      code: "out_of_range",
      message: `Set listings must include between ${CHAIR_SET_MIN} and ${CHAIR_SET_MAX} ${seatingProductNoun(subcategoryId, true).toLowerCase()}.`,
    });
    return issues;
  }

  const capacityIssue = validatePerChairCapacities(capacities, subcategoryId);
  if (capacityIssue) {
    issues.push(capacityIssue);
  }

  return issues;
}

export function getChairSpecFormValidationMessage(
  subcategoryId: string,
  form: ChairSpecFormState,
): string | undefined {
  const attrs = buildChairAttributesFromForm(subcategoryId, form);
  const issues = validateStoredChairAttributes(subcategoryId, attrs);

  if (isChairMaterialOnlySubcategory(subcategoryId)) {
    if (!form.chairMaterial) {
      return `${seatingProductNoun(subcategoryId)} material is required.`;
    }
    return issues[0]?.message;
  }

  if (!form.chairMaterial) {
    return `${seatingProductNoun(subcategoryId)} material is required.`;
  }

  if (sofaRequiresShapeField(subcategoryId) && !isSofaShape(form.sofaShape)) {
    return "Please choose standard or L-shaped sofa.";
  }

  if (sofaUsesSimplifiedLShapedSeating(subcategoryId, form.sofaShape)) {
    return issues[0]?.message;
  }

  if (!form.chairQuantityMode) {
    const unit = seatingProductNoun(subcategoryId).toLowerCase();
    return `Please choose single ${unit} or set of ${unit}s.`;
  }

  if (form.chairQuantityMode === "single") {
    if (form.chairSeatCapacities[0] == null) {
      return "Seating capacity is required.";
    }
  } else {
    const count = normalizeChairSetCount(form.chairSetCount);
    const slice = form.chairSeatCapacities.slice(0, count);
    if (slice.some((c) => c == null)) {
      const unit = seatingProductNoun(subcategoryId).toLowerCase();
      return `Enter seating capacity for each ${unit} in the set.`;
    }
  }

  return issues[0]?.message;
}

export function composeChairTitleFromAttributes(
  attributes: ListingSpecAttributes | Record<string, unknown>,
  subcategoryId = "chair",
  identity?: ListingTitleIdentity | null,
): string {
  const record = attributes as Record<string, unknown>;
  if (!isChairMaterial(record.chair_material)) {
    return "";
  }

  const materialLabel = listingTitleMaterialWord(
    record.chair_material,
    formatChairMaterialLabel,
  );
  const mode = record.chair_quantity_mode;
  const capacities = parseCapacitiesArray(record.chair_seat_capacities);
  const product = seatingProductNoun(subcategoryId);
  const products = seatingProductNoun(subcategoryId, true);
  const lShapedPrefix =
    subcategoryId === "sofa-couche" && record.sofa_shape === "l_shaped"
      ? "L-shaped"
      : "";

  if (mode === "single" && capacities && capacities.length === 1) {
    return composeListingTitleFromSlotsWithIdentity(
      {
        form: lShapedPrefix || undefined,
        product: joinListingTitleParts(
          capacities[0],
          "Seater",
          materialLabel,
          product,
        ),
      },
      identity,
    );
  }

  if (mode === "set" && capacities && capacities.length >= CHAIR_SET_MIN) {
    const capacitySegment = `${capacities.join("-")} Seater`;
    const count = capacities.length;
    const productWord = count === 1 ? product : products;
    return composeListingTitleFromSlotsWithIdentity(
      {
        form: lShapedPrefix || undefined,
        product: joinListingTitleParts(
          capacitySegment,
          "Set of",
          count,
          materialLabel,
          productWord,
        ),
      },
      identity,
    );
  }

  if (lShapedPrefix) {
    return composeListingTitleFromSlotsWithIdentity(
      {
        form: lShapedPrefix,
        product: joinListingTitleParts(materialLabel, product),
      },
      identity,
    );
  }

  return "";
}

export function composeLShapedChairTitleFromAttributes(
  attributes: ListingSpecAttributes | Record<string, unknown>,
  subcategoryId: string = "l-shaped-chair",
  identity?: ListingTitleIdentity | null,
): string {
  const record = attributes as Record<string, unknown>;
  if (!isChairMaterial(record.chair_material)) {
    return "";
  }
  const shapeLabel =
    getSeatingSpecUiVariant(subcategoryId) === "sofa"
      ? "L-shaped Sofa"
      : "L-shaped Chair";
  return composeListingTitleFromSlotsWithIdentity(
    {
      form: listingTitleMaterialWord(
        record.chair_material,
        formatChairMaterialLabel,
      ),
      product: shapeLabel,
    },
    identity,
  );
}

export function composeScopedChairLikeTitle(
  subcategoryId: string | undefined,
  attributes: ListingSpecAttributes | Record<string, unknown> | undefined,
  identity?: ListingTitleIdentity | null,
): string {
  if (!subcategoryId || !attributes) return "";
  if (isFullChairSpecSubcategory(subcategoryId)) {
    return composeChairTitleFromAttributes(attributes, subcategoryId, identity);
  }
  if (isChairMaterialOnlySubcategory(subcategoryId)) {
    return composeLShapedChairTitleFromAttributes(
      attributes,
      subcategoryId,
      identity,
    );
  }
  return "";
}

export function resolveListingAttributesForSubmit(input: {
  subcategoryId: string;
  sizeText?: string | null;
  chairSpec?: ChairSpecFormState;
  diningTableChairSpec?: DiningTableChairSpecFormState;
  deriveFromSize?: (
    subcategoryId: string,
    sizeText: string,
  ) => ListingSpecAttributes;
}): ListingSpecAttributes {
  if (isChairLikeSubcategory(input.subcategoryId)) {
    return buildChairAttributesFromForm(
      input.subcategoryId,
      input.chairSpec ?? defaultChairSpecFormState(),
    );
  }
  if (isDiningTableChairSubcategory(input.subcategoryId)) {
    return buildDiningTableChairAttributesFromForm(
      input.diningTableChairSpec ?? defaultDiningTableChairSpecFormState(),
    );
  }
  if (input.deriveFromSize) {
    return input.deriveFromSize(
      input.subcategoryId,
      String(input.sizeText ?? "").trim(),
    );
  }
  return {};
}
